import React from 'react';
import {
  DayAvailability,
  WeekdayKey,
  WEEKDAYS,
} from '../../CustomBlocks/slotBooking/slotTypes';
import { CopyIcon } from '../../shared/Icons';
import { SettingsGroup, Switch } from './settingsUi';
import { DayRow, TextButton } from './SlotBookingEditor.styles';

interface WeeklyHoursEditorProps {
  value: Record<WeekdayKey, DayAvailability>;
  onChange: (next: Record<WeekdayKey, DayAvailability>) => void;
}

const WeeklyHoursEditor: React.FC<WeeklyHoursEditorProps> = ({ value, onChange }) => {
  const update = (day: WeekdayKey, patch: Partial<DayAvailability>) =>
    onChange({ ...value, [day]: { ...value[day], ...patch } });

  const firstEnabled = WEEKDAYS.find(({ key }) => value[key].enabled);
  const isInvalid = (key: WeekdayKey) =>
    value[key].enabled && Boolean(value[key].start && value[key].end) && value[key].end <= value[key].start;
  const invalidDays = WEEKDAYS.filter(({ key }) => isInvalid(key)).map(({ label }) => label.slice(0, 3));

  const copyToEnabledDays = () => {
    if (!firstEnabled) return;
    const { start, end } = value[firstEnabled.key];
    const next = { ...value };
    WEEKDAYS.forEach(({ key }) => {
      if (next[key].enabled) next[key] = { ...next[key], start, end };
    });
    onChange(next);
  };

  return (
    <SettingsGroup
      title="Weekly hours"
      footer={
        invalidDays.length
          ? `End time must be after start time (${invalidDays.join(', ')}). No slots are offered on these days.`
          : undefined
      }
      footerTone="danger"
      action={
        <TextButton
          type="button"
          disabled={!firstEnabled}
          onClick={copyToEnabledDays}
          title={firstEnabled ? `Copy ${firstEnabled.label}'s hours to every available day` : undefined}
        >
          <CopyIcon size={13} />
          Apply {firstEnabled ? firstEnabled.label.slice(0, 3) : ''} to all
        </TextButton>
      }
    >
      {WEEKDAYS.map(({ key, label }) => {
        const hours = value[key];
        const inputClass = `sb-pill-input${isInvalid(key) ? ' invalid' : ''}`;
        return (
          <DayRow key={key}>
            <Switch
              checked={hours.enabled}
              label={`${label} available`}
              onChange={(enabled) => update(key, { enabled })}
            />
            <span className={`sb-day-name${hours.enabled ? '' : ' off'}`} title={label}>
              {label.slice(0, 3)}
            </span>
            <div className="sb-day-times">
              {hours.enabled ? (
                <>
                  <input
                    type="time"
                    className={inputClass}
                    value={hours.start}
                    onChange={(e) => update(key, { start: e.target.value })}
                    aria-label={`${label} start`}
                  />
                  –
                  <input
                    type="time"
                    className={inputClass}
                    value={hours.end}
                    onChange={(e) => update(key, { end: e.target.value })}
                    aria-label={`${label} end`}
                  />
                </>
              ) : (
                <span className="sb-day-off">Unavailable</span>
              )}
            </div>
          </DayRow>
        );
      })}
    </SettingsGroup>
  );
};

export default WeeklyHoursEditor;
